import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { StockFiguresScope } from '../productFilters/branchScopeMeta';
import { stockScopeCopy } from './productDetailMeta';
import { asSpan } from './productDetailTypography';

type StockScopeNoteProps = {
    /** Whose stock the variants' figures count. */
    stockFigures: StockFiguresScope;
};

/**
 * The line under the Variants heading (bos-app#119): whose stock the
 * quantities, cost and batches below count. Everything above it — the name,
 * the prices, the barcodes — is the product's own and reads the same for
 * every branch.
 *
 * One muted Typography holds the icon and the words, so the icon takes the
 * muted tone as its current colour rather than from a class of its own.
 */
export const StockScopeNote: React.FC<StockScopeNoteProps> = ({
    stockFigures,
}) => {
    const { t } = useTranslation('pages');
    const scope = stockScopeCopy(stockFigures);

    return (
        <Typography
            className="inline-flex items-center gap-1.5"
            color="muted"
            render={asSpan}
            type="body-xs"
            {...getNodeTestIdProp('product-detail-stock-scope')}
        >
            <Icon
                aria-hidden
                className="size-3 shrink-0"
                icon="solar:shop-2-linear"
            />
            {t(scope.keys.note, scope.values)}
        </Typography>
    );
};
